/**
 * PaymentOptionsModal — split-payment amount picker.
 *
 * Opened by any Pay Now click in PortalBilling. Lets a wholesale customer
 * choose a partial amount (default = full balance) before handing off to
 * HelcimPay.js, where they pick Credit Card or ACH inside the Helcim modal.
 *
 * There is NO rail chooser here. It was removed 2026-09-10: a single-rail
 * checkout (cc-only or ach-only) is rejected by Helcim with 400 because Fee
 * Saver requires cc-ach, and this control was the only thing that could ask
 * for one. The Helcim widget already offers both card and bank and states
 * the card fee itself, so the chooser was pure friction. See
 * quickbooks_views.portal_invoice_pay, which hard-pins the mint to cc-ach.
 *
 * See docs/SPLIT_PAYMENTS.md for broader context.
 */
import React, { useState, useEffect } from 'react';
import { PortalInvoice } from '../../services/quickbooksService';

export interface PaymentSubmitParams {
  amount: string;            // decimal as string
}

interface PaymentOptionsModalProps {
  isOpen: boolean;
  invoice: PortalInvoice | null;
  onClose: () => void;
  onPaymentSubmit: (params: PaymentSubmitParams) => Promise<void>;
}

const formatCurrency = (amount: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

// Client-side validation mirroring the backend pay endpoint:
// positive, <= balance_due, max 2 decimal places. Returns an error
// string or null when valid.
const validateAmount = (raw: string, balance: number): string | null => {
  const trimmed = raw.trim();
  if (trimmed === '') return 'Enter an amount to pay';
  if (!/^\d*\.?\d*$/.test(trimmed)) return 'Enter a valid dollar amount';
  const num = parseFloat(trimmed);
  if (isNaN(num)) return 'Enter a valid dollar amount';
  if (num <= 0) return 'Amount must be greater than $0';
  const dot = trimmed.indexOf('.');
  if (dot !== -1 && trimmed.length - dot - 1 > 2) {
    return 'Amount cannot have fractional cents (max 2 decimal places)';
  }
  // Tiny epsilon so an exact-balance entry isn't rejected by float drift.
  if (num > balance + 1e-9) return 'Amount cannot exceed the balance due';
  return null;
};

const PaymentOptionsModal: React.FC<PaymentOptionsModalProps> = ({
  isOpen,
  invoice,
  onClose,
  onPaymentSubmit,
}) => {
  const [amount, setAmount] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Reset to full balance each time the modal opens for an invoice.
  useEffect(() => {
    if (isOpen && invoice) {
      setAmount(invoice.balance_due);
      setSubmitting(false);
      setSubmitError(null);
    }
  }, [isOpen, invoice]);

  if (!isOpen || !invoice) return null;

  const balance = parseFloat(invoice.balance_due) || 0;
  const error = validateAmount(amount, balance);
  const amountNum = parseFloat(amount);
  // Live "remaining after this payment". Falls back to the full balance when
  // the input is invalid so the row never shows a nonsensical figure.
  const remaining = !error && !isNaN(amountNum) ? balance - amountNum : balance;
  const isPartial = !error && remaining > 0;

  const handleContinue = async () => {
    if (error || submitting) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      await onPaymentSubmit({ amount: amount.trim() });
    } catch (e) {
      // Surface a failed /pay/ INSIDE the modal — the page-level banner is
      // occluded by this overlay — and keep the modal open so the customer
      // can retry. (The concurrent-ACH 409 this used to name is gone:
      // the guard was removed in cc0ba6c1.)
      const msg =
        (e as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (e as Error)?.message ||
        'Payment could not be started. Please try again.';
      setSubmitError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.overlay} role="dialog" aria-modal="true" aria-label="Payment Options">
      <div style={styles.content}>
        <div style={styles.header}>
          <h2 style={styles.title}>Payment Options</h2>
          <button onClick={onClose} style={styles.close} aria-label="Close">×</button>
        </div>

        <div style={styles.body}>
          <div style={styles.invoiceLine}>
            Invoice <strong>{invoice.invoice_number}</strong>
          </div>
          <div style={styles.balanceLine}>
            Balance Due: <strong>{formatCurrency(balance)}</strong>
          </div>

          {/* ── Amount to pay ── */}
          <label htmlFor="payment-amount" style={styles.sectionLabel}>Amount to pay</label>
          <div style={styles.amountWrap}>
            <span style={styles.dollarSign}>$</span>
            <input
              id="payment-amount"
              type="text"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              style={{
                ...styles.amountInput,
                borderColor: error ? '#dc2626' : '#d1d5db',
              }}
              aria-invalid={!!error}
              aria-describedby={error ? 'payment-amount-error' : 'payment-remaining'}
            />
          </div>

          <div id="payment-remaining" data-testid="remaining-balance" style={styles.remainingRow}>
            Remaining after this payment: <strong>{formatCurrency(remaining)}</strong>
            {isPartial && <span style={styles.partialTag}> (partial payment)</span>}
          </div>

          {error && (
            <div id="payment-amount-error" role="alert" style={styles.error}>
              {error}
            </div>
          )}

          {submitError && (
            <div role="alert" style={styles.submitError}>
              {submitError}
            </div>
          )}
        </div>

        <div style={styles.footer}>
          <button onClick={onClose} style={styles.cancelButton} disabled={submitting}>
            Cancel
          </button>
          <button
            onClick={handleContinue}
            disabled={!!error || submitting}
            style={{
              ...styles.continueButton,
              opacity: error || submitting ? 0.5 : 1,
              cursor: error || submitting ? 'not-allowed' : 'pointer',
            }}
          >
            {submitting ? 'Processing…' : 'Continue to Payment →'}
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: '1rem',
  },
  content: {
    backgroundColor: 'white', borderRadius: '12px', width: '100%',
    maxWidth: '460px', maxHeight: '90vh', overflow: 'auto',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '1.25rem', borderBottom: '1px solid #e5e7eb',
  },
  title: { fontSize: '1.25rem', fontWeight: 700, color: '#1f2937', margin: 0 },
  close: {
    background: 'none', border: 'none', fontSize: '1.5rem',
    color: '#6b7280', cursor: 'pointer', padding: '0.25rem', lineHeight: 1,
  },
  body: { padding: '1.25rem' },
  invoiceLine: { fontSize: '0.95rem', color: '#374151', marginBottom: '0.25rem' },
  balanceLine: { fontSize: '1rem', color: '#1f2937', marginBottom: '1.25rem' },
  sectionLabel: {
    display: 'block', fontSize: '0.8rem', fontWeight: 700, color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.03em',
    margin: '1rem 0 0.5rem',
  },
  amountWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  dollarSign: {
    position: 'absolute', left: '0.75rem', color: '#6b7280',
    fontSize: '1rem', fontWeight: 600,
  },
  amountInput: {
    width: '100%', padding: '0.625rem 0.75rem 0.625rem 1.5rem',
    fontSize: '1.05rem', border: '1px solid #d1d5db', borderRadius: '8px',
    boxSizing: 'border-box',
  },
  remainingRow: { marginTop: '0.5rem', fontSize: '0.9rem', color: '#374151' },
  partialTag: { color: '#508590', fontWeight: 600 },
  error: { marginTop: '0.5rem', color: '#dc2626', fontSize: '0.85rem' },
  submitError: {
    marginTop: '1rem', padding: '0.625rem 0.75rem',
    backgroundColor: '#fef2f2', border: '1px solid #fecaca',
    borderRadius: '8px', color: '#dc2626', fontSize: '0.85rem',
  },
  helperNote: { marginTop: '1rem', fontSize: '0.85rem', color: '#6b7280' },
  footer: {
    display: 'flex', justifyContent: 'flex-end', gap: '0.75rem',
    padding: '1rem 1.25rem', borderTop: '1px solid #e5e7eb',
  },
  cancelButton: {
    padding: '0.625rem 1.25rem', fontSize: '0.95rem', fontWeight: 500,
    backgroundColor: '#f3f4f6', color: '#374151',
    border: '1px solid #d1d5db', borderRadius: '8px', cursor: 'pointer',
  },
  continueButton: {
    padding: '0.625rem 1.25rem', fontSize: '0.95rem', fontWeight: 600,
    background: 'linear-gradient(135deg, #2D5A27 0%, #4A7C42 100%)',
    color: 'white', border: 'none', borderRadius: '8px',
  },
};

export default PaymentOptionsModal;
